import { Quaternion, Vector3 } from 'three';
import * as TWEEN from '@tweenjs/tween.js';
import { prefersReducedMotion } from '@infinitform/shared-ui';
import { nearestNamedView } from './view-names';

export type OrbitStep = 'up' | 'down' | 'left' | 'right';
export type RollStep = 'cw' | 'ccw';
export type CubeStep = OrbitStep | RollStep;

/** One arrow press or arrow key turns the view by this much. */
export const STEP_ANGLE = Math.PI / 4;

/** Length of a cube-driven camera move. */
export const MOVE_MS = 280;

const WORLD_UP = new Vector3(0, 0, 1);
const TOP_BOTTOM_UP = new Vector3(0, 1, 0);

/** The camera and orbit target a move changes. */
export interface CameraRig {
  camera: { position: Vector3; up: Vector3; lookAt(target: Vector3): void };
  controls: { target: Vector3; update(): void };
}

interface ActiveMove {
  tween: TWEEN.Tween<{ t: number }>;
  finish: () => void;
}

const activeMoves = new WeakMap<object, ActiveMove>();

/** Tween length for a camera move: none when the OS asks for reduced motion. */
export function moveDuration(ms: number): number {
  return prefersReducedMotion() ? 0 : ms;
}

/** Up vector for a view along `direction`: Y when looking straight down or up, Z otherwise. */
export function viewUp(direction: Vector3): Vector3 {
  return Math.abs(direction.clone().normalize().z) > 0.92 ? TOP_BOTTOM_UP.clone() : WORLD_UP.clone();
}

/** Left/right turn about world Z, up/down about the camera's side axis, roll about the view direction. */
export function stepRotation(eye: Vector3, up: Vector3, step: CubeStep): Quaternion {
  const axis = new Vector3();
  if (step === 'left' || step === 'right') {
    axis.copy(WORLD_UP);
    if (step === 'left') axis.negate();
  } else if (step === 'up' || step === 'down') {
    axis.crossVectors(up.clone().normalize(), eye.clone().normalize()).normalize();
    if (step === 'down') axis.negate();
  } else {
    axis.copy(eye).normalize();
    if (step === 'ccw') axis.negate();
  }
  return new Quaternion().setFromAxisAngle(axis, STEP_ANGLE);
}

/** Eye (camera minus target) and up after one step. */
export function stepEndPose(eye: Vector3, up: Vector3, step: CubeStep): { eye: Vector3; up: Vector3 } {
  const rotation = stepRotation(eye, up, step);
  return {
    eye: eye.clone().applyQuaternion(rotation),
    up: up.clone().applyQuaternion(rotation).normalize(),
  };
}

/** Direction from the orbit target toward the camera. */
export function viewDirection(rig: CameraRig): Vector3 {
  return rig.camera.position.clone().sub(rig.controls.target).normalize();
}

function finishActiveMove(rig: CameraRig): void {
  const active = activeMoves.get(rig.controls);
  if (!active) return;
  activeMoves.delete(rig.controls);
  active.tween.stop();
  active.finish();
}

/**
 * Turns the camera about the orbit target by `rotation`, easing up toward
 * `targetUp` and the target toward `targetPoint`. A move still running on
 * the same rig is finished first. Instant under reduced motion.
 */
export function animateRotation(
  rig: CameraRig,
  rotation: Quaternion,
  targetUp: Vector3,
  targetPoint: Vector3 | null,
  ms: number = MOVE_MS,
): void {
  finishActiveMove(rig);
  const { camera, controls } = rig;
  const startEye = camera.position.clone().sub(controls.target);
  const startUp = camera.up.clone();
  const startTarget = controls.target.clone();
  const endTarget = targetPoint ? targetPoint.clone() : startTarget.clone();
  const endUp = targetUp.clone().normalize();

  const apply = (t: number): void => {
    const target = startTarget.clone().lerp(endTarget, t);
    const eye = startEye.clone().applyQuaternion(new Quaternion().slerp(rotation, t));
    controls.target.copy(target);
    camera.position.copy(target).add(eye);
    camera.up.copy(startUp).lerp(endUp, t).normalize();
    camera.lookAt(target);
    controls.update();
  };

  const duration = moveDuration(ms);
  if (duration === 0) {
    apply(1);
    return;
  }

  const state = { t: 0 };
  const tween = new TWEEN.Tween(state)
    .to({ t: 1 }, duration)
    .easing(TWEEN.Easing.Quadratic.InOut)
    .onUpdate(() => apply(state.t))
    .onComplete(() => activeMoves.delete(rig.controls))
    .start();
  activeMoves.set(rig.controls, { tween, finish: () => apply(1) });
}

/** One 45° orbit or roll step. */
export function stepView(rig: CameraRig, step: CubeStep): void {
  finishActiveMove(rig);
  const eye = rig.camera.position.clone().sub(rig.controls.target);
  const rotation = stepRotation(eye, rig.camera.up, step);
  animateRotation(rig, rotation, rig.camera.up.clone().applyQuaternion(rotation), null);
}

/** Looks at the target, or at `targetPoint`, from `direction`, keeping the distance. */
export function rotateToDirection(rig: CameraRig, direction: Vector3, targetPoint?: Vector3): void {
  finishActiveMove(rig);
  const to = direction.clone().normalize();
  const rotation = new Quaternion().setFromUnitVectors(viewDirection(rig), to);
  animateRotation(rig, rotation, viewUp(to), targetPoint ?? null);
}

export type CubeSide = 'posX' | 'negX' | 'posY' | 'negY' | 'posZ' | 'negZ';

const SIDE_DIRECTIONS: Record<CubeSide, Vector3> = {
  posX: new Vector3(1, 0, 0),
  negX: new Vector3(-1, 0, 0),
  posY: new Vector3(0, 1, 0),
  negY: new Vector3(0, -1, 0),
  posZ: new Vector3(0, 0, 1),
  negZ: new Vector3(0, 0, -1),
};

/** Looks at the target square-on from one side of the cube. */
export function rotateToSide(rig: CameraRig, side: CubeSide): void {
  rotateToDirection(rig, SIDE_DIRECTIONS[side]);
}

/** Turns to the closest of the named views. */
export function snapToNearestView(rig: CameraRig): void {
  finishActiveMove(rig);
  rotateToDirection(rig, nearestNamedView(viewDirection(rig)).direction);
}
